import {
  Provider,
  RETURNS_ORDER_SYNC_PROVIDERS,
} from "@redotech/redo-order-common/provider";
import {
  CustomIntegrationOrderChangesSchema,
  type CustomIntegrationOrderChanges,
} from "@redotech/redo-return-core-common/custom-return-integration-order";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

import type { Contract, Target } from "../types";

export const orderSyncInputSchema = z.object({
  platform: createEnum(RETURNS_ORDER_SYNC_PROVIDERS, {
    enumName: "ReturnsOrderSyncProvider",
  }).describe("The system the order comes from."),
  // Loose so one function can handle every platform; it branches on `platform`.
  rawOrder: z
    .looseObject({})
    .describe(
      "The order as the source system returned it, before Redo converts it.",
    ),
});
export type OrderSyncInput = z.infer<typeof orderSyncInputSchema>;

export const orderSyncOrderSchema =
  CustomIntegrationOrderChangesSchema.describe(
    "Changes to the order Redo syncs, in the public order schema. Line items match rawOrder's line item ids and fulfillments its shipments (Commerce Cloud: productItems[].itemId, shipments[].shipmentNo; Aptos: OrderItems.Item[].ItemID). Applies to Commerce Cloud and Aptos orders; other platforms ignore it. Commerce Cloud ignores line item image and images.",
  );
export type OrderSyncOrder = CustomIntegrationOrderChanges;

// An explicit `skip` flag, not an undefined return, so a function that forgets
// to return fails validation instead of silently dropping every order.
export const orderSyncOutputSchema = z.object({
  skip: z
    .boolean()
    .optional()
    .describe("Set to true to skip syncing this order into Redo."),
  skipReason: z
    .string()
    .optional()
    .describe("The reason the order was skipped, for logging purposes."),
  order: orderSyncOrderSchema.optional(),
});
export type OrderSyncOutput = z.infer<typeof orderSyncOutputSchema>;

export const orderSyncTarget = {
  id: "returns.order-sync",
  displayName: "Order sync",
  description:
    "Runs before Redo syncs an order from an external system. It can skip the order or change its fields.",
} as const satisfies Target;

export const orderSyncStarterCode = `// Edit this function. It runs before Redo syncs each order from an external system.
// Return {} to sync the order, or { skip: true, skipReason } to skip it.
// For Commerce Cloud and Aptos orders, return { order } to change the synced order's fields.
export default async (input: Input): Promise<Output> => {
  return {};
};
`;

export const orderSyncContractV1: Contract = {
  id: `${orderSyncTarget.id}@2026-09.1`,
  targetId: orderSyncTarget.id,
  inputSchema: orderSyncInputSchema,
  outputSchema: orderSyncOutputSchema,
  tools: [],
  sampleInput: {
    platform: Provider.APTOS,
    rawOrder: {
      OrderID: "1048576",
      OrderNumber: "100245",
      OrderExternalOrderID: "W100245",
      OrderType: "Web",
      OrderDate: "2026-09-18T14:02:11",
      LastModifiedDate: "2026-09-19T09:30:00",
      OrderStatus: "Shipped",
      OrderAmountShipping: 5,
      OrderAmountTax: 3.2,
      OrderAmountTotal: 48.2,
      CurrencyCode: "USD",
      OrderShippingAddress1: "123 Main St",
      OrderShippingCity: "San Francisco",
      OrderShippingStateProvince: "CA",
      OrderShippingZip: "94110",
      FirstName: "Jane",
      LastName: "Doe",
    },
  } satisfies OrderSyncInput,
};
